import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { formatIDR, formatIDRShort, formatDate } from "../lib/format";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Area, AreaChart,
  BarChart, Bar, LabelList,
} from "recharts";
import { AlertTriangle, RefreshCw, ExternalLink, TrendingUp, TrendingDown, Home, Pencil, Bell, Calendar, Wallet, CheckSquare, UsersRound } from "lucide-react";
import { hasFinancialAccess, isMuaAdmin, isManagement } from "../lib/constants";
import AssignPicModal from "../components/AssignPicModal";

const PERIOD_LABELS = { this_month: "Bulan Ini", last_month: "Bulan Lalu", this_year: "Tahun Ini", custom: "Kustom" };
const METRICS = [
  { key: "booking_value",    label: "Nilai Booking",    color: "#8B262D", isMoney: true },
  { key: "booking_count",    label: "Jumlah Booking",   color: "#5A6B7C", isMoney: false },
  { key: "verified_payment", label: "Pembayaran Masuk", color: "#2E7D5A", isMoney: true },
];

const fmt = (n, money) => (money ? formatIDR(n || 0) : new Intl.NumberFormat("id-ID").format(n || 0));
const fmtAxis = (n, money) => (money ? formatIDRShort(n || 0) : new Intl.NumberFormat("id-ID").format(n || 0));

const StatCard = ({ label, value, delta, tone = "default", testId }) => (
  <Card className="p-4 border border-[#E5E0D8] bg-white shadow-none" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold">{label}</div>
    <div className={`mt-1 font-display text-2xl font-bold ${tone === "danger" ? "text-rose-700" : "text-[#1E2022]"}`}>{value}</div>
    {delta && (
      <div className="mt-1 text-xs flex items-center gap-1">
        {delta.prev_zero ? (
          <span className="text-[#8C9096]">Belum ada pembanding</span>
        ) : (
          <>
            {delta.pct >= 0 ? <TrendingUp className="w-3 h-3 text-emerald-600"/> : <TrendingDown className="w-3 h-3 text-rose-600"/>}
            <span className={delta.pct >= 0 ? "text-emerald-700" : "text-rose-700"}>{delta.pct >= 0 ? "+" : ""}{delta.pct}%</span>
            <span className="text-[#8C9096]">vs periode sebelumnya</span>
          </>
        )}
      </div>
    )}
  </Card>
);

const PanelCard = ({ title, subtitle, caption, action, children }) => (
  <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none">
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="min-w-0">
        <h3 className="font-display text-sm font-bold text-[#1E2022]">{title}</h3>
        {subtitle && <div className="text-[11px] text-[#8C9096] mt-0.5">{subtitle}</div>}
      </div>
      {action}
    </div>
    {children}
    {caption && <div className="mt-2 text-[10px] text-[#8C9096] italic">{caption}</div>}
  </Card>
);

const EmptyState = ({ text = "Belum ada data pada periode ini" }) => (
  <div className="h-40 flex items-center justify-center text-xs text-[#8C9096]">{text}</div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("this_month");
  const [metric, setMetric] = useState("booking_value");
  const [analytics, setAnalytics] = useState(null);
  const [bookingsAll, setBookingsAll] = useState([]);
  const [schedulesAll, setSchedulesAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [picModalOpen, setPicModalOpen] = useState(false);

  const load = () => {
    setLoading(true); setError(null);
    Promise.all([
      api.get(`/dashboard/analytics?period=${period}`),
      api.get(`/bookings`).catch(() => ({ data: [] })),
      api.get(`/schedules`).catch(() => ({ data: [] })),
    ])
      .then(([a, b, s]) => { setAnalytics(a.data); setBookingsAll(b.data || []); setSchedulesAll(s.data || []); setLoading(false); })
      .catch((e) => { setError(e?.response?.data?.detail || "Gagal memuat data"); setLoading(false); });
  };
  useEffect(load, [period]);

  const currentMetric = METRICS.find((m) => m.key === metric);
  const trend = useMemo(() => (analytics?.sales_trend || []).map((r) => ({ ...r, dateLabel: (r.date || "").slice(5) })), [analytics]);

  if (loading) return <div className="p-8 text-sm text-[#8C9096]">Memuat dashboard...</div>;
  if (error) return (
    <div className="p-8 text-center" data-testid="dashboard-error">
      <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-2"/>
      <div className="text-sm font-semibold text-rose-700 mb-1">Gagal memuat dashboard</div>
      <div className="text-xs text-[#8C9096] mb-3">{error}</div>
      <Button onClick={load} size="sm" variant="outline"><RefreshCw className="w-3.5 h-3.5 mr-1.5"/> Coba Lagi</Button>
    </div>
  );

  const { totals, sales_by_division, receivable_aging, leads_trend, lead_sources, event_schedule, task_status, my_tasks, notes } = analytics;
  const aging = [
    { label: "Belum Jatuh Tempo",   amount: receivable_aging.not_due.amount,          count: receivable_aging.not_due.count,          color: "#5A6B7C" },
    { label: "Terlambat 1–7 Hari",  amount: receivable_aging.overdue_1_7.amount,      count: receivable_aging.overdue_1_7.count,      color: "#D19A3D" },
    { label: "Terlambat 8–30 Hari", amount: receivable_aging.overdue_8_30.amount,     count: receivable_aging.overdue_8_30.count,     color: "#C05F2B" },
    { label: ">30 Hari",             amount: receivable_aging.overdue_31_plus.amount, count: receivable_aging.overdue_31_plus.count, color: "#8B262D" },
  ];

  const HomeShortcut = ["owner","general_manager"].includes(user?.role) && (
    <Card className="p-5 border border-[#E5E0D8] bg-gradient-to-br from-[#FAF6EE] to-white shadow-none">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-11 h-11 rounded-lg bg-brand/10 flex items-center justify-center shrink-0"><Home className="w-5 h-5 text-brand"/></div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[#8C9096]">Situs Publik</div>
            <h3 className="font-display text-base font-bold text-[#1E2022]">Kelola Homepage</h3>
          </div>
        </div>
        <div className="flex gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E0D8] bg-white text-xs font-medium hover:bg-[#FAF6EE]"><ExternalLink className="w-3.5 h-3.5"/> Lihat</a>
          <Link to="/landing-cms" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-brand text-white text-xs font-semibold hover:bg-brand/90"><Pencil className="w-3.5 h-3.5"/> Kelola</Link>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="dashboard-visual">
      {/* Header + Period Filter */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#1E2022]">Dasbor</h1>
          <p className="text-xs text-[#5C6066] mt-0.5">
            Ringkasan visual DWG OS · {isMuaAdmin(user) && <span className="text-brand font-semibold">Admin Kantor Pusat · </span>}
            Periode {PERIOD_LABELS[period]} ({formatDate(analytics.period.start)} – {formatDate(analytics.period.end)})
          </p>
        </div>
        <div className="flex flex-wrap gap-1 bg-white p-1 rounded-md border border-[#E5E0D8]" data-testid="dashboard-period-filter">
          {Object.entries(PERIOD_LABELS).filter(([k]) => k !== "custom").map(([k, v]) => (
            <button key={k} onClick={() => setPeriod(k)}
              className={`px-3 h-8 rounded text-xs font-medium transition-colors ${period === k ? "bg-brand text-white" : "text-[#5C6066] hover:bg-[#FAF6EE]"}`}
              data-testid={`period-${k}`}>{v}</button>
          ))}
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Nilai Booking" value={formatIDR(totals.booking_value)} delta={totals.delta_booking_value} testId="kpi-booking-value"/>
        <StatCard label="Jumlah Booking" value={new Intl.NumberFormat("id-ID").format(totals.booking_count)} testId="kpi-booking-count"/>
        <StatCard label="Pembayaran Masuk" value={formatIDR(totals.verified_payment)} testId="kpi-verified-payment"/>
        <StatCard label="Piutang Aktif" value={formatIDRShort(aging.reduce((a,b)=>a+b.amount, 0))} testId="kpi-receivable" tone={aging[3].amount > 0 ? "danger" : "default"}/>
      </div>

      {HomeShortcut}

      {/* Akses Cepat — Fitur Booking-Centric Task System */}
      <Card className="p-3 sm:p-4 border border-[#E5E0D8] bg-gradient-to-br from-[#FAF6EE] to-white shadow-none" data-testid="dashboard-quick-launch">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-display text-sm font-bold text-[#1E2022]">Akses Cepat</h3>
            <p className="text-[11px] text-[#5C6066]">Alur baru: konsultasi → inquiry → booking → tugas otomatis → pantau progres</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Link to="/inquiries/pool" className="p-3 rounded-md bg-white border border-[#E5E0D8] hover:border-[#B7A175] hover:shadow-sm transition-all" data-testid="dash-quick-inquiry-pool">
            <div className="text-[10px] uppercase tracking-wider text-[#8C9096] font-semibold">Client & Penjualan</div>
            <div className="font-display font-bold text-sm mt-0.5 text-[#1E2022]">Inquiry Pool</div>
            <div className="text-[11px] text-[#5C6066] mt-1">Semua form konsultasi masuk real-time</div>
          </Link>
          <Link to="/bookings/tasks" className="p-3 rounded-md bg-white border border-[#E5E0D8] hover:border-[#B7A175] hover:shadow-sm transition-all" data-testid="dash-quick-bookings-tasks">
            <div className="text-[10px] uppercase tracking-wider text-[#8C9096] font-semibold">Eksekusi</div>
            <div className="font-display font-bold text-sm mt-0.5 text-[#1E2022]">Tugas per Booking</div>
            <div className="text-[11px] text-[#5C6066] mt-1">Progres tugas & pembayaran per acara</div>
          </Link>
          <Link to="/tasks/board" className="p-3 rounded-md bg-white border border-[#E5E0D8] hover:border-[#B7A175] hover:shadow-sm transition-all" data-testid="dash-quick-tasks-board">
            <div className="text-[10px] uppercase tracking-wider text-[#8C9096] font-semibold">Kanban</div>
            <div className="font-display font-bold text-sm mt-0.5 text-[#1E2022]">Board Tugas</div>
            <div className="text-[11px] text-[#5C6066] mt-1">Tersedia → Diambil → Dikerjakan → Selesai</div>
          </Link>
          <Link to="/master-products" className="p-3 rounded-md bg-white border border-[#E5E0D8] hover:border-[#B7A175] hover:shadow-sm transition-all" data-testid="dash-quick-master-products">
            <div className="text-[10px] uppercase tracking-wider text-[#8C9096] font-semibold">Katalog</div>
            <div className="font-display font-bold text-sm mt-0.5 text-[#1E2022]">Master Paket & Task Template</div>
            <div className="text-[11px] text-[#5C6066] mt-1">Atur paket + auto-generate tugas</div>
          </Link>
        </div>
      </Card>

      {/* Perlu Perhatian — 4 slot fix (selalu tampil walau nilai 0) */}
      {(() => {
        // Slot 1: Tagihan Terlambat
        const overdueCount = (receivable_aging.overdue_1_7?.count || 0) + (receivable_aging.overdue_8_30?.count || 0) + (receivable_aging.overdue_31_plus?.count || 0);
        const overdueAmount = (receivable_aging.overdue_1_7?.amount || 0) + (receivable_aging.overdue_8_30?.amount || 0) + (receivable_aging.overdue_31_plus?.amount || 0);
        // Slot 2 & 3: dari bookings client-side
        const today = new Date().toISOString().slice(0,10);
        const in7 = new Date(Date.now() + 7*86400000).toISOString().slice(0,10);
        // Selaraskan dengan kalender: semua status kecuali Cancelled dihitung.
        const bookingsIn7 = bookingsAll.filter((b) =>
          b.status !== "Cancelled" && b.event_date && b.event_date >= today && b.event_date <= in7
        ).length;
        const schedulesIn7 = schedulesAll.filter((s) =>
          s.status !== "cancelled" && s.scheduled_at && s.scheduled_at.slice(0,10) >= today && s.scheduled_at.slice(0,10) <= in7
        ).length;
        const upcoming7 = bookingsIn7 + schedulesIn7;
        const noPic = bookingsAll.filter((b) =>
          b.status !== "Cancelled" && b.event_date && b.event_date >= today &&
          !(b.assigned_admin || "").trim() &&
          !((b.coordinator_user_ids || []).length)
        ).length;
        // Slot 4: Tugas menunggu tindakan (org-wide, dari task_status agregat; fallback my_tasks)
        const openFromStatus = (task_status || []).filter((s) => s.status !== "Done").reduce((sum, s) => sum + (s.count || 0), 0);
        const openTasks = openFromStatus || (my_tasks || []).length;

        const slots = [
          {
            key: "overdue", label: "Tagihan Terlambat",
            value: `${overdueCount} tagihan`,
            sub: overdueCount === 0 ? "Semua tagihan on-track"
                 : (hasFinancialAccess(user) ? formatIDR(overdueAmount) : "Perlu tindak lanjut"),
            to: "/invoices", icon: Wallet,
            tone: overdueCount > 0 ? "danger" : "muted", testId: "attn-overdue",
          },
          {
            key: "upcoming7", label: "Acara 7 Hari ke Depan",
            value: `${upcoming7} agenda`,
            sub: upcoming7 === 0 ? "Belum ada agenda terdekat" : `${bookingsIn7} acara + ${schedulesIn7} jadwal — cek kesiapan tim`,
            to: "/calendar", icon: Calendar,
            tone: upcoming7 > 0 ? "warn" : "muted", testId: "attn-upcoming",
          },
          {
            key: "no_pic", label: "Booking Tanpa PIC",
            value: `${noPic} booking`,
            sub: noPic === 0 ? "Semua booking sudah ditugaskan" : "Klik untuk triase & tetapkan PIC",
            action: noPic > 0 && isManagement(user) ? () => setPicModalOpen(true) : null,
            to: noPic > 0 && isManagement(user) ? null : "/bookings",
            icon: UsersRound,
            tone: noPic > 0 ? "danger" : "muted", testId: "attn-no-pic",
          },
          {
            key: "open_tasks", label: "Tugas Menunggu Tindakan",
            value: `${openTasks} tugas`,
            sub: openTasks === 0 ? "Semua tugas terselesaikan" : "Segera ambil tindakan",
            to: "/tasks", icon: CheckSquare,
            tone: openTasks > 0 ? "warn" : "muted", testId: "attn-tasks",
          },
        ];

        const toneCls = (t) => t === "danger" ? "border-rose-200 bg-rose-50"
                            : t === "warn"   ? "border-amber-200 bg-amber-50"
                            : t === "info"   ? "border-sky-200 bg-sky-50"
                            : "border-[#E5E0D8] bg-[#FAF6EE]";
        const dotCls = (t) => t === "danger" ? "bg-rose-500"
                            : t === "warn"   ? "bg-amber-500"
                            : t === "info"   ? "bg-sky-500"
                            : "bg-[#B7B1A5]";

        return (
          <Card className="p-4 sm:p-5 border border-[#E5E0D8] bg-white shadow-none" data-testid="dashboard-perlu-perhatian">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4 text-brand" />
              <h3 className="font-display text-sm font-bold text-[#1E2022]">Perlu Perhatian</h3>
              <span className="text-[11px] text-[#8C9096]">Empat hal yang perlu dipantau setiap hari</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
              {slots.map((it) => {
                const Ic = it.icon;
                const inner = (
                  <div className="flex items-start gap-2">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${dotCls(it.tone)}`}/>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-widest text-[#5C6066] font-semibold">{it.label}</div>
                      <div className="font-display text-sm font-bold text-[#1E2022] mt-0.5 flex items-center gap-1.5"><Ic className="w-3.5 h-3.5"/>{it.value}</div>
                      <div className="text-[11px] text-[#5C6066] mt-0.5 truncate">{it.sub}</div>
                    </div>
                  </div>
                );
                const cls = `block p-3 rounded-md border ${toneCls(it.tone)} hover:shadow-sm transition-shadow text-left w-full`;
                if (it.action) {
                  return (
                    <button key={it.key} onClick={it.action} data-testid={it.testId} className={cls}>{inner}</button>
                  );
                }
                return (
                  <Link key={it.key} to={it.to} data-testid={it.testId} className={cls}>{inner}</Link>
                );
              })}
            </div>
          </Card>
        );
      })()}

      {/* Main Sales Trend */}
      <PanelCard
        title="Tren Penjualan"
        subtitle={`${currentMetric.label} · ${formatDate(analytics.period.start)} – ${formatDate(analytics.period.end)}`}
        caption="Booking dihitung berdasarkan tanggal dibuat. Pembayaran berdasarkan tanggal masuk. Booking yang dibatalkan tidak dihitung."
        action={
          <div className="flex gap-1 bg-[#FAF6EE] p-1 rounded-md" data-testid="trend-metric-toggle">
            {METRICS.map((m) => (
              <button key={m.key} onClick={() => setMetric(m.key)}
                className={`px-2.5 h-7 rounded text-[11px] font-medium transition-colors ${metric === m.key ? "bg-white text-[#1E2022] shadow-sm" : "text-[#5C6066] hover:text-[#1E2022]"}`}
                data-testid={`metric-${m.key}`}>{m.label}</button>
            ))}
          </div>
        }
      >
        {trend.length === 0 ? <EmptyState/> : (
          <div className="h-64" data-testid="chart-sales-trend">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="mainGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={currentMetric.color} stopOpacity={0.28}/>
                    <stop offset="95%" stopColor={currentMetric.color} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false}/>
                <XAxis dataKey="dateLabel" tick={{ fontSize: 10, fill: "#8C9096" }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 10, fill: "#8C9096" }} axisLine={false} tickLine={false} width={60}
                  tickFormatter={(v) => fmtAxis(v, currentMetric.isMoney)}/>
                <Tooltip formatter={(v) => fmt(v, currentMetric.isMoney)}
                  labelFormatter={(l, p) => (p?.[0]?.payload?.date ? formatDate(p[0].payload.date) : l)}
                  contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #E5E0D8" }}/>
                <Area type="monotone" dataKey={metric} stroke={currentMetric.color} strokeWidth={2}
                  fillOpacity={1} fill="url(#mainGrad)" name={currentMetric.label}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </PanelCard>

      {/* Supporting panels grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PanelCard title="Penjualan per Divisi" subtitle="Dari booking aktif periode ini" caption={notes.multi_div}
          action={<Link to="/reports" className="text-[11px] text-brand hover:underline">Lihat Laporan →</Link>}>
          {sales_by_division.length === 0 ? <EmptyState/> : (
            <div className="h-64" data-testid="chart-sales-by-division">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sales_by_division} layout="vertical" margin={{ top: 5, right: 24, left: 8, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" horizontal={false}/>
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#8C9096" }} axisLine={false} tickLine={false} tickFormatter={(v)=>formatIDRShort(v)}/>
                  <YAxis type="category" dataKey="short" tick={{ fontSize: 10, fill: "#5C6066" }} axisLine={false} tickLine={false} width={70}/>
                  <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ fontSize: 11 }}/>
                  <Bar dataKey="value" fill="#8B262D" radius={[0,4,4,0]} name="Nilai" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </PanelCard>

        <PanelCard title="Piutang Aktif — Aging" subtitle="Snapshot berdasarkan jatuh tempo pembayaran atau tanggal acara" caption="Snapshot piutang saat ini. Jika jatuh tempo tidak diisi, gunakan tanggal acara sebagai patokan."
          action={<Link to="/finance" className="text-[11px] text-brand hover:underline">Lihat Detail →</Link>}>
          <div className="h-64" data-testid="chart-receivable-aging">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={aging} margin={{ top: 12, right: 16, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#5C6066" }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={50}/>
                <YAxis tick={{ fontSize: 10, fill: "#8C9096" }} axisLine={false} tickLine={false} tickFormatter={(v)=>formatIDRShort(v)} width={60}/>
                <Tooltip formatter={(v, _n, p) => [formatIDR(v), `${p?.payload?.count || 0} booking`]} contentStyle={{ fontSize: 11 }}/>
                <Bar dataKey="amount" radius={[4,4,0,0]}>
                  {aging.map((a, i) => (
                    <Bar key={i} fill={a.color}/>
                  ))}
                  <LabelList dataKey="count" position="top" fill="#5C6066" fontSize={10}/>
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </PanelCard>

        <PanelCard title="Tren Calon Client Baru" subtitle="Enquiry masuk per hari"
          action={<Link to="/inquiry" className="text-[11px] text-brand hover:underline">Lihat Enquiry →</Link>}>
          {leads_trend.length === 0 ? <EmptyState/> : (
            <div className="h-56" data-testid="chart-leads-trend">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={leads_trend.map(r => ({ ...r, dateLabel: r.date.slice(5) }))} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false}/>
                  <XAxis dataKey="dateLabel" tick={{ fontSize: 10, fill: "#8C9096" }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fontSize: 10, fill: "#8C9096" }} axisLine={false} tickLine={false} allowDecimals={false} width={30}/>
                  <Tooltip contentStyle={{ fontSize: 11 }}/>
                  <Line type="monotone" dataKey="count" stroke="#5A6B7C" strokeWidth={2} dot={{ r: 3 }} name="Enquiry"/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </PanelCard>

        <PanelCard title="Sumber Calon Client" subtitle="Top 8 sumber periode ini">
          {lead_sources.length === 0 ? <EmptyState/> : (
            <div className="h-56" data-testid="chart-lead-sources">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={lead_sources} layout="vertical" margin={{ top: 5, right: 24, left: 8, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" horizontal={false}/>
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#8C9096" }} axisLine={false} tickLine={false} allowDecimals={false}/>
                  <YAxis type="category" dataKey="source" tick={{ fontSize: 10, fill: "#5C6066" }} axisLine={false} tickLine={false} width={110}/>
                  <Tooltip contentStyle={{ fontSize: 11 }}/>
                  <Bar dataKey="count" fill="#2E7D5A" radius={[0,4,4,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </PanelCard>

        <PanelCard title="Jadwal Acara 90 Hari Ke Depan" subtitle="Booking aktif per minggu"
          action={<Link to="/calendar" className="text-[11px] text-brand hover:underline">Lihat Kalender →</Link>}>
          {event_schedule.length === 0 ? <EmptyState/> : (
            <div className="h-56" data-testid="chart-event-schedule">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={event_schedule.map(r => ({ ...r, label: r.week.slice(5) }))} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false}/>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#8C9096" }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fontSize: 10, fill: "#8C9096" }} axisLine={false} tickLine={false} allowDecimals={false} width={30}/>
                  <Tooltip contentStyle={{ fontSize: 11 }} labelFormatter={(l,p)=>(p?.[0]?.payload?.week ? `Minggu ${formatDate(p[0].payload.week)}` : l)}/>
                  <Bar dataKey="count" fill="#8B262D" radius={[4,4,0,0]} name="Acara"/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </PanelCard>

        <PanelCard title="Progres Tugas" subtitle="Distribusi status tugas divisi/perusahaan"
          action={<Link to="/tasks" className="text-[11px] text-brand hover:underline">Lihat Tugas →</Link>}>
          {task_status.length === 0 ? <EmptyState/> : (
            <div className="h-56" data-testid="chart-task-status">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={task_status} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false}/>
                  <XAxis dataKey="status" tick={{ fontSize: 10, fill: "#5C6066" }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fontSize: 10, fill: "#8C9096" }} axisLine={false} tickLine={false} allowDecimals={false} width={30}/>
                  <Tooltip contentStyle={{ fontSize: 11 }}/>
                  <Bar dataKey="count" fill="#5A6B7C" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </PanelCard>
      </div>

      {/* My Tasks list — bukan tabel laporan panjang */}
      <PanelCard title="Tugas Saya" subtitle="Tugas terdekat & terlambat"
        action={<Link to="/tasks" className="text-[11px] text-brand hover:underline">Semua Tugas →</Link>}>
        {my_tasks.length === 0 ? (
          <div className="text-xs text-[#8C9096] py-6 text-center">Tidak ada tugas terbuka untuk Anda. 🎉</div>
        ) : (
          <ul className="divide-y divide-[#F0EBE0]" data-testid="my-tasks-list">
            {my_tasks.map((t) => {
              const overdue = t.due_date && t.due_date < new Date().toISOString().slice(0,10);
              return (
                <li key={t.id} className="py-2.5 flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${overdue ? "bg-rose-500" : "bg-emerald-500"}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1E2022] truncate">{t.title || "(tanpa judul)"}</div>
                    <div className="text-[11px] text-[#8C9096]">
                      {t.due_date ? `Tenggat ${formatDate(t.due_date)}` : "Tanpa tenggat"}
                      {t.priority && ` · Prioritas ${t.priority}`}
                      {t.status && ` · ${t.status}`}
                    </div>
                  </div>
                  {t.booking_id && <Link to={`/bookings/${t.booking_id}`} className="text-[11px] text-brand hover:underline shrink-0">Detail →</Link>}
                </li>
              );
            })}
          </ul>
        )}
      </PanelCard>

      <AssignPicModal
        open={picModalOpen}
        setOpen={setPicModalOpen}
        bookings={bookingsAll}
        onSaved={() => { load(); }}
      />
    </div>
  );
}
